export interface IAddress {
	Line1: string,
	Line2: string,
	Line3: string,
	City: string,
	State: string,
	PostalCode: string,
	Country: string
}

export interface ICountryConfiguration {
	StreetNumber: 'before' | 'after' | 'after_with_comma';
	Line2: string[],
	City: string[],
	State: string[]
	ShortStateName: boolean;
}

// Configuration for address component types based on country code. The order of component types determines the priority (first match used)
const countryConfig: Record<string, ICountryConfiguration> = {
	AU: { StreetNumber: 'before', Line2: ['subpremise'], City: ['locality'], State: ['administrative_area_level_1'], ShortStateName: true },
	CA: { StreetNumber: 'before', Line2: ['subpremise'], City: ['locality'], State: ['administrative_area_level_1'], ShortStateName: false },
	DE: { StreetNumber: 'before', Line2: ['subpremise'], City: ['locality'], State: [], ShortStateName: false },
	DK: { StreetNumber: 'after', Line2: ['subpremise'], City: ['locality'], State: [], ShortStateName: false },
	FR: { StreetNumber: 'before', Line2: ['subpremise'], City: ['locality'], State: [], ShortStateName: false },
	GB: { StreetNumber: 'before', Line2: ['neighborhood', 'locality'], City: ['postal_town'], State: ['administrative_area_level_2'], ShortStateName: false },
	IE: { StreetNumber: 'before', Line2: ['subpremise'], City: ['postal_town'], State: [], ShortStateName: false },
	IT: { StreetNumber: 'after_with_comma', Line2: ['subpremise'], City: ['locality'], State: ['administrative_area_level_2'], ShortStateName: true },
	NL: { StreetNumber: 'after', Line2: ['subpremise'], City: ['administrative_area_level_2'], State: ['administrative_area_level_1'], ShortStateName: false },
	NO: { StreetNumber: 'before', Line2: ['subpremise'], City: ['postal_town'], State: [], ShortStateName: false },
	NZ: { StreetNumber: 'before', Line2: ['sublocality_level_1'], City: ['locality'], State: ['administrative_area_level_1'], ShortStateName: false },
	PH: { StreetNumber: 'before', Line2: ['subpremise'], City: ['locality'], State: ['administrative_area_level_2'], ShortStateName: false },
	SE: { StreetNumber: 'after', Line2: ['subpremise'], City: ['postal_town'], State: [], ShortStateName: false },
	SG: { StreetNumber: 'before', Line2: ['subpremise'], City: ['neighborhood'], State: [], ShortStateName: false },
	US: { StreetNumber: 'before', Line2: ['subpremise'], City: ['sublocality', 'locality', 'administrative_area_level_3', 'administrative_area_level_2'], State: ['administrative_area_level_1'], ShortStateName: false },
	ZA: { StreetNumber: 'before', Line2: ['sublocality'], City: ['locality'], State: ['administrative_area_level_1'], ShortStateName: false },
	default: { StreetNumber: 'before', Line2: ['subpremise'], City: ['postal_town', 'locality', 'administrative_area_level_2'], State: ['administrative_area_level_1'], ShortStateName: false }
};

// Finds the first matching address component based on the specified component types.
const findAddressComponent = (
	components: google.maps.places.AddressComponent[],
	componentTypes: string[]
): google.maps.places.AddressComponent => {
	for (const type of componentTypes) {
		const component = components.find(component => component.types.includes(type));
		if (component) return component;
	}
	return { shortText: '', longText: '', types: [] };
};

// Retrieves the configuration for a given country code.
const getCountryConfig = (code: string): ICountryConfiguration => {
	return countryConfig[code] || countryConfig.default;
};

// Assigns values to the address object based on the address component types for the country.
const assignAddressComponents = (
	address: IAddress,
	addressProperties: string[],
	components: google.maps.places.AddressComponent[],
	countryConfig: ICountryConfiguration
): void => {
	Object.entries(countryConfig).forEach(([key, componentTypes]) => {
		if (!addressProperties.includes(key))
			return;

		const component = findAddressComponent(components, componentTypes);

		const useShortName = key === 'State' && countryConfig.ShortStateName;
		address[key] = (useShortName) ? component.shortText : component.longText;
	});
};

// Main method to populate an address object with data from Google Maps Geocoder address components.
export const getAddressFromComponents = (
	address: IAddress,
	components: google.maps.places.AddressComponent[],
	addressProperties: string[]
): IAddress => {
	const countryCode = components.find(component => component.types.includes('country'))?.shortText;
	const countryConfig = getCountryConfig(countryCode);

	assignAddressComponents(address, addressProperties, components, countryConfig);

	// Handle street number and route concatenation.
	const streetNumber = findAddressComponent(components, ['street_number']).longText;
	const route = findAddressComponent(components, ['route']).longText;
	switch (countryConfig.StreetNumber) {
		case 'before':
			address.Line1 = `${streetNumber} ${route}`.trim();
			break;
		case 'after':
			address.Line1 = `${route} ${streetNumber}`.trim();
			break;
		case 'after_with_comma':
			address.Line1 = `${route}, ${streetNumber}`.trim();
			break;
	}

	address.PostalCode = findAddressComponent(components, ['postal_code']).longText;
	address.Country = findAddressComponent(components, ['country']).longText;

	return address;
};
